import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

type Props = {
  onReply: () => void;
  onCopy: () => void;
  onTogglePin: () => void;
  onToggleStar: () => void;
  isPinned: boolean;
  isStarred: boolean;
  onReact: (emoji: string) => void;
  alignRight?: boolean;
};

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👏', '🔥'];

export function MessageActions({
  onReply,
  onCopy,
  onTogglePin,
  onToggleStar,
  isPinned,
  isStarred,
  onReact,
  alignRight,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [reactOpen, setReactOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen && !reactOpen) return;
    function onDoc(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) {
        setMenuOpen(false);
        setReactOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen, reactOpen]);

  return (
    <div
      ref={ref}
      className={cn(
        'absolute top-0 flex gap-1 transition-opacity',
        reactOpen || menuOpen
          ? 'visible opacity-100 z-10'
          : 'invisible opacity-0 group-hover:visible group-hover:opacity-100',
        alignRight ? '-left-2 -translate-x-full' : '-right-2 translate-x-full',
      )}
    >
      <button
        type="button"
        onClick={() => setReactOpen((v) => !v)}
        className="rounded-full bg-white p-1.5 text-gray-500 shadow-sm hover:bg-gray-50"
        aria-label="React"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onReply}
        className="rounded-full bg-white p-1.5 text-gray-500 shadow-sm hover:bg-gray-50"
        aria-label="Reply"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
        >
          <polyline points="9 17 4 12 9 7" />
          <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        className="rounded-full bg-white p-1.5 text-gray-500 shadow-sm hover:bg-gray-50"
        aria-label="More"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="19" cy="12" r="1.6" />
        </svg>
      </button>

      {reactOpen ? (
        <div className="absolute -top-12 left-0 flex gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 shadow-md">
          {QUICK_REACTIONS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                onReact(e);
                setReactOpen(false);
              }}
              className="text-lg hover:scale-125"
            >
              {e}
            </button>
          ))}
        </div>
      ) : null}

      {menuOpen ? (
        <div className="absolute top-full right-0 mt-1 w-36 overflow-hidden rounded-md border border-gray-200 bg-white text-sm shadow-lg">
          <MenuItem
            onClick={() => {
              onCopy();
              setMenuOpen(false);
            }}
          >
            Copy text
          </MenuItem>
          <MenuItem
            onClick={() => {
              onTogglePin();
              setMenuOpen(false);
            }}
          >
            {isPinned ? 'Unpin' : 'Pin message'}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onToggleStar();
              setMenuOpen(false);
            }}
          >
            {isStarred ? 'Unstar' : 'Star message'}
          </MenuItem>
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50"
    >
      {children}
    </button>
  );
}
